import { Fragment, useMemo, useState } from 'react';
import {
  buildGroupChart,
  clubsDrawnNote,
  defaultSelectedClubIds,
  excludedClubsNote,
  groupChartDefs,
  GROUP_CHARTS_INTRO,
  GROUP_CHART_FORMS,
  MAX_CHART_CLUBS,
  partialBucketNote,
  rankClubsForCharts,
  resolveChartForm,
  CLUB_PALETTE_DARK,
  CLUB_PALETTE_LIGHT,
  type GroupChartDef,
  type GroupChartForm,
} from '../../../../../lib/portalReport/groupCharts';
import type {
  PortalGroupClub,
  PortalGroupSeries,
  PortalReportLabels,
} from '../../../../../types/portalReport';
import { Band, SectionHeading, SwitchGroup } from './controls';
import { GroupBarChart } from './GroupBarChart';

// CLIENT PORTAL §A — the three group charts: grouped volume bars, a cost line
// per club, thirteen months.
//
// ⚠️⚠️ THE CHART'S CONTENTS CHANGE WITH THE CHIPS, so two owners can be looking
// at the same report and see different lines. That is a defensible trade for an
// owner-driven snapshot — but only because each chart NAMES the clubs it is
// drawing in its own subhead (`clubsDrawnNote`) rather than relying on the
// reader remembering what they ticked.
//
// ⚠️ THE TOGGLE IS A REAL FILTER, NOT OPACITY. An unticked club leaves the model
// entirely, so both axes rescale around what is left; dimming it instead would
// keep every remaining bar squashed under a maximum the reader can no longer
// see.
//
// ⚠️ COLOUR = CLUB, MARK = MEASURE — a club's bars and its line are one colour,
// and the mark type says which axis a value belongs to without the legend. The
// legend names both anyway: identity is never carried by mark type alone.

export interface GroupChartsBandProps {
  series: PortalGroupSeries;
  labels: PortalReportLabels;
  mode: 'light' | 'dark';
}

export function GroupChartsBand({ series, labels, mode }: GroupChartsBandProps) {
  // ⚠️ Ranked ONCE, from the payload, so the ordering — and therefore every
  // club's colour — cannot depend on what is ticked. See `rankClubsForCharts`.
  const { drawable, excluded } = useMemo(() => rankClubsForCharts(series.clubs), [series.clubs]);
  const [selected, setSelected] = useState<string[]>(() => defaultSelectedClubIds(drawable));
  const selectedIds = useMemo(() => new Set(selected), [selected]);
  const palette = mode === 'dark' ? CLUB_PALETTE_DARK : CLUB_PALETTE_LIGHT;

  const defs = useMemo(
    // ⚠️ The two CRM charts are ABSENT — not empty — for a scope with no CRM
    // link. An empty chart reads as a measured nothing.
    () => groupChartDefs(labels).filter((d) => series.hasCrm || !d.crm),
    [labels, series.hasCrm],
  );

  if (drawable.length === 0 || defs.length === 0) return null;

  const excludedNote = excludedClubsNote(excluded);
  // ⚠️ The first chart's title, for the FIRST chip row's accessible name. Read off
  // `defs` rather than restated, so the two rows are named by the charts they
  // actually sit above even if the set changes.
  const firstTitle = (defs[0] as GroupChartDef).title;
  const partialNote = partialBucketNote(series);

  const toggle = (accountId: string): void => {
    setSelected((prev) =>
      prev.includes(accountId) ? prev.filter((id) => id !== accountId) : [...prev, accountId],
    );
  };

  return (
    <Band>
      <SectionHeading
        title="Your locations month by month"
        // ⚠️ LEADS the band — a caveat read after a number has been believed is
        // not a caveat, which is this report's oldest rule about where an
        // explanation goes.
        intro={GROUP_CHARTS_INTRO}
      />
      {partialNote && <p className="rpt-note rpt-muted">{partialNote}</p>}

      {/* ⚠️⚠️ ONE STATE, TWO RENDERED INSTANCES — see `ClubToggle`. This one sits
          where it always has; the second is between the cards, above the chart it
          is nearest. */}
      <ClubToggle
        drawable={drawable}
        selectedIds={selectedIds}
        palette={palette}
        onToggle={toggle}
        aboveTitle={firstTitle}
      />
      {/* ⚠️ The clubs that cannot be drawn are NAMED, with the reason, rather
          than quietly missing from a list the reader assumes is complete. */}
      {excludedNote && <p className="rpt-note rpt-muted">{excludedNote}</p>}

      {selected.length === 0 && (
        <p className="rpt-note rpt-muted" data-testid="no-clubs-selected">
          No locations are selected, so there is nothing to draw. Tick one above.
        </p>
      )}

      {/*
        ⚠️⚠️ THE THREE CARDS NEED A GAPPED WRAPPER. They were direct siblings of
        `<Band>`, which is not a flex or grid container, and `.rpt-card` carries
        background, radius and padding but NO MARGIN — so the three merged into
        one another with no separation at all.

        ⚠️ THE GAP MUST NOT GO ON `.rpt-card`. It is used report-wide, and a
        bottom margin there would reflow every other band on the page.

        ⚠️ AND THIS IS `.rpt-card-grid`, NOT A NEW CLASS. Its base is already
        exactly a gapped one-column stack of cards at the report's own 14px
        scale — the `--2` / `--3` / `--4` modifiers are what add columns, and
        three full-width charts want none. Declaring a second wrapper with the
        same gap would be two spacing scales one edit away from disagreeing; the
        CRM-over-time band and this one now cannot drift, because they are the
        same class.
      */}
      {selected.length > 0 && (
        <div className="rpt-card-grid">
          {defs.map((def, i) => (
            <Fragment key={def.key}>
              {/*
                ⚠️⚠️ OUTSIDE AND ABOVE THE CARD, NEVER IN THE CARD HEAD — because
                POSITION IS WHAT EXPRESSES SCOPE here, and two controls of different
                scope are about to sit near each other.

                The chips are SHARED: unticking a club on one chart unticks it on
                both. The `Both · Bars · Lines` switch in the card head is PER CARD,
                deliberately (round 6 §4d) — a reader can have bars on one and lines
                on the other. Put the repeated chips in the card head and a reader who
                has learnt "these chips affect everything" will reasonably assume the
                switch beside them does too.

                ⚠️ SO THE RULE IS: BAND-LEVEL CONTROLS IN THE BAND, CARD-LEVEL
                CONTROLS IN THE CARD. Do NOT reconcile the two by making the layer
                switch global; per-card was a deliberate decision and it is useful.

                ⚠️ `i > 0` rather than a hardcoded second instance: the chips belong
                above every chart after the first. With the Ad Conversions card gone
                (round 6 §4e) there are exactly two charts, so this is ONE extra
                instance today rather than a pattern that multiplies.
              */}
              {i > 0 && (
                <ClubToggle
                  drawable={drawable}
                  selectedIds={selectedIds}
                  palette={palette}
                  onToggle={toggle}
                  aboveTitle={def.title}
                />
              )}
              <GroupChartCard
                def={def}
                series={series}
                drawable={drawable}
                selectedIds={selectedIds}
                mode={mode}
              />
            </Fragment>
          ))}
        </div>
      )}
    </Band>
  );
}

/**
 * The club chips.
 *
 * ⚠️⚠️ RENDERED TWICE FROM ONE STATE (§6). Scott: "they will function as 1. A club
 * ticked off on leads is the same on the sales chart." So this is a CONTROLLED
 * component with no state of its own — there is nothing to synchronise, because
 * there is only ever one answer.
 *
 * ⚠️ TWO INSTANCES OF ONE CONTROL NEED DISTINCT ACCESSIBLE NAMES. Both are
 * `role="group"`, and two groups called "Locations shown" on one page leave a
 * screen-reader user unable to tell which they have landed in — so each is named
 * for the chart it sits above.
 *
 * ⚠️ The chips carry NO DOM `id` (the `key` is React's, not the document's), so
 * there is nothing to make unique per instance. If one ever gains an `id` or an
 * `aria-controls`, it has to be suffixed per instance or the duplicate will
 * silently point both rows at the same target.
 */
function ClubToggle({
  drawable,
  selectedIds,
  palette,
  onToggle,
  aboveTitle,
}: {
  drawable: readonly PortalGroupClub[];
  selectedIds: ReadonlySet<string>;
  palette: readonly string[];
  onToggle: (accountId: string) => void;
  aboveTitle: string;
}) {
  return (
    <div className="rpt-club-toggle" role="group" aria-label={`Locations shown, above ${aboveTitle}`}>
      {drawable.map((club, i) => {
        const on = selectedIds.has(club.accountId);
        // ⚠️ COLOUR IS THE CLUB'S RANK IN `drawable`, not its place in the
        // selection, so unticking one cannot repaint the others. Both rows index
        // the same array, so the two are the same colour by construction.
        const colour = palette[i % palette.length] as string;
        return (
          <button
            key={club.accountId}
            type="button"
            aria-pressed={on}
            className={`rpt-club-chip${on ? ' rpt-club-chip-on' : ''}`}
            onClick={() => onToggle(club.accountId)}
          >
            <span
              className="rpt-swatch"
              style={{ background: on ? colour : 'transparent', boxShadow: `inset 0 0 0 2px ${colour}` }}
            />
            {club.name}
          </button>
        );
      })}
    </div>
  );
}
/**
 * One chart card.
 *
 * ⚠️⚠️ ITS OWN COMPONENT BECAUSE THE FORM SWITCH IS PER CARD (§4d). A `useState`
 * cannot live inside a `.map()`, and one switch shared across the cards would be
 * the wrong control anyway: whether the bars or the line is worth looking at is
 * a judgement about ONE metric, unlike the grain, which is one question asked of
 * a whole band.
 */
function GroupChartCard({
  def,
  series,
  drawable,
  selectedIds,
  mode,
}: {
  def: GroupChartDef;
  series: PortalGroupSeries;
  drawable: readonly PortalGroupClub[];
  selectedIds: ReadonlySet<string>;
  mode: 'light' | 'dark';
}) {
  // ⚠️ DEFAULTS TO `both`, which is the chart round 5 shipped — the switch adds a
  // way to simplify it, and a default that hid half of it would change what the
  // report says rather than how it can be read.
  const [form, setForm] = useState<GroupChartForm>('both');
  const chart = buildGroupChart(def, series, drawable, selectedIds);
  const layers = resolveChartForm(chart, form);
  const drawn = clubsDrawnNote(chart.bars);

  return (
    <section className="rpt-card" data-testid={`group-chart-${def.key}`}>
      {/* ⚠️ `.rpt-section-head` IS THE REPORT'S EXISTING title-plus-controls
          row (flex, space-between, baseline-aligned). The first draft invented
          an `rpt-card-head` class that does not exist in the stylesheet, so the
          heading and the switch stacked instead of sharing a line — found in a
          browser, invisible to jsdom. */}
      <div className="rpt-section-head">
        <h3 className="rpt-h3">{def.title}</h3>
        {/* ⚠️ THE SWITCH SITS IN THE CARD HEAD, where every other per-card
            control on this report sits (`ReportChartCard`). */}
        <SwitchGroup
          ariaLabel={`What to draw on the ${def.title} chart`}
          value={form}
          onChange={setForm}
          options={GROUP_CHART_FORMS}
        />
      </div>
      {/* ⚠️⚠️ THE SUBHEAD NAMES THE CLUBS ON THE CHART. Its contents change with
          the chips, so without this two readers of the same report have no way
          to know they are looking at different figures. */}
      {drawn && <p className="rpt-note rpt-muted">{drawn}</p>}
      {/* ⚠️⚠️ AND THE LEGEND LINE DESCRIBES WHAT IS ACTUALLY DRAWN. It read
          "Bars: … (right). Line: … (left)." unconditionally, which in `Bars`
          named an axis that had just been removed — a legend describing the
          model rather than the chart is worse than none. */}
      <p className="rpt-note rpt-muted" data-testid={`legend-${def.key}`}>
        {layers.showBars && `Bars: ${def.volumeLabel} (right).`}
        {layers.showBars && layers.showCost ? ' ' : ''}
        {layers.showCost && `Line: ${def.costLabel} (left).`}
      </p>
      {/* ⚠️⚠️ THE WITHHELD COST AXIS IS STATED BEFORE THE CHART, not after it. A
          reader who has already read the bars as the whole story does not go
          looking for a footnote.

          ⚠️ Not repeated in `Bars`, where the reader removed the line themselves
          and an explanation of a currency problem would read as a second,
          different fault. */}
      {form !== 'bars' && chart.costAxisWithheldReason && (
        <p className="rpt-note rpt-muted" data-testid={`cost-withheld-${def.key}`}>
          {chart.costAxisWithheldReason}
        </p>
      )}
      <GroupBarChart
        chart={chart}
        buckets={series.buckets}
        mode={mode}
        partialFinalBucket={series.partialFinalBucket}
        form={form}
      />
      {/* ⚠️⚠️ A WITHHELD SERIES IS STATED, never left as a missing line. The
          reader ticked that club, and a chart that silently ignores a tick reads
          as broken software. */}
      {chart.withheld.map((w) => (
        <p key={w.name} className="rpt-note rpt-muted">
          {w.name} is not on the line — {w.reason}.
        </p>
      ))}
    </section>
  );
}
